import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell, hasRole } from './components/AppShell'
import { AuthProvider, useAuth } from './components/AuthProvider'
import { DraftGuardProvider } from './components/DraftGuard'
import { environment } from './lib/env'
import { AccessDeniedPage, AuthCallbackPage, LandingPage } from './pages/Authentication'
import { AdminPage } from './pages/Admin'
import { ApprovalsPage } from './pages/Approvals'
import { DashboardPage } from './pages/Dashboard'
import { ExpensesPage } from './pages/Expenses'
import { TimePage } from './pages/Time'
import type { Role } from './types'

const RequireAuthentication = () => {
  const { session } = useAuth()
  const location = useLocation()
  if (!session) return <Navigate to="/" state={{ from: location }} replace />
  if (session.user.roles.length === 0) return <Navigate to="/access-denied" replace />
  return <Outlet />
}

const RequireRole = ({ role }: { role: Role }) => {
  const { session } = useAuth()
  if (!session || !hasRole(session.user.roles, role)) return <Navigate to="/access-denied" replace />
  return <Outlet />
}

const ProtectedWorkspace = () => (
  <DraftGuardProvider>
    <AppShell />
  </DraftGuardProvider>
)

const NotFoundPage = () => <main className="not-found-page"><div><span className="brand-mark"><span>Q</span></span><p className="eyebrow">NOT FOUND</p><h1>That page isn’t in this workspace.</h1><a className="button button-primary" href="/">Return home</a></div></main>

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<LandingPage />} />
    <Route path="/auth/callback" element={<AuthCallbackPage />} />
    <Route path="/access-denied" element={<AccessDeniedPage />} />
    <Route element={<RequireAuthentication />}>
      <Route element={<ProtectedWorkspace />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route element={<RequireRole role="Employee" />}>
          <Route path="/time" element={<TimePage />} />
          <Route path="/expenses" element={<ExpensesPage />} />
        </Route>
        <Route element={<RequireRole role="Manager" />}>
          <Route path="/approvals" element={<ApprovalsPage />} />
        </Route>
        <Route element={<RequireRole role="Admin" />}>
          <Route path="/admin" element={<AdminPage />} />
        </Route>
      </Route>
    </Route>
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
)

export const App = () => (
  <BrowserRouter basename={environment.appBasePath}>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
)
